import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, type ReactElement } from "react";
import { authenticatedApi } from "@/lib/api";
import { Badge } from "@/components/badge";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import { RdHeaderStrip } from "@/components/v2/rd-header-strip";
import { FormsSurface } from "@/components/v2/forms-surface";
import type { FormProfileData } from "@/components/v2/forms-profile-card";
import {
  FormSnapshotBody,
  formSnapshotOriginLine,
  priorWeeklySnapshot,
  snapshotWeekLabel,
} from "@/components/v2/form-snapshot-detail";
import { useRdDetail, type RdDetailData } from "@/lib/use-rd-detail";
import { canRenameRd } from "@/lib/permissions";
import type { AlwaysAiUser } from "../../../shared/api-types";
import type { FormSnapshotData } from "../../../shared/form-types";
import { AskAboutSourceAction } from "@/components/v2/ask-about-source-action";

function SurfaceLoading(): ReactElement {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-muted-foreground">Loading...</p>
    </div>
  );
}

/**
 * The viewer, for the rename affordance on this page's header strip. Reuses
 * the app-wide `["current-user"]` entry `WorkspaceShell` has already settled
 * before any route renders, so this mounts an observer rather than blocking on
 * a fetch. Closed while unresolved.
 */
function useCanRenameRd(): boolean {
  const { getToken } = useAuth();
  const me = useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });

  return me.data ? canRenameRd(me.data.user) : false;
}

/**
 * `?profile=setup` (the Snapshot's setup link, NO-669) opens setup once. After
 * the surface has mounted with it, drop it from the URL in place, so a cancel
 * followed by a reload or Back does not reopen an empty setup form.
 */
function useConsumeProfileSetupParam(consume: boolean): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (!consume) return;

    void navigate({
      to: ".",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        profile: undefined,
      }),
      replace: true,
    });
  }, [consume, navigate]);
}

/**
 * Functional Forms surface — the RD workspace record section for the weekly
 * form profile + snapshot history.
 */
export function FormsSurfacePage(): ReactElement | null {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const search = useSearch({ strict: false }) as { edit?: string; profile?: "setup" };
  const { getToken } = useAuth();
  const rd = useRdDetail(rdId);
  const canRename = useCanRenameRd();
  const profile = useQuery({
    queryKey: ["forms-profile", rdId],
    queryFn: () =>
      authenticatedApi<{ data: FormProfileData | null }>(
        getToken,
        `/api/rds/${rdId}/forms/profile`
      ),
  });
  const snapshots = useQuery({
    queryKey: ["forms", rdId],
    queryFn: () =>
      authenticatedApi<{ data: FormSnapshotData[] }>(
        getToken,
        `/api/rds/${rdId}/forms`
      ),
  });

  const detail = rd.data?.data;
  const loaded = Boolean(detail && profile.data && snapshots.data);
  useConsumeProfileSetupParam(loaded && search.profile === "setup");
  if (!detail || !profile.data || !snapshots.data) return <SurfaceLoading />;

  // `?edit=` deep link (the chat flyover's Edit action). Validated against the
  // loaded list so a stale or foreign id degrades to the plain list view.
  const initialEditFormId =
    search.edit && snapshots.data.data.some((s) => s.id === search.edit)
      ? search.edit
      : null;

  return (
    <FormsSurface
      rdId={detail.id}
      canRename={canRename}
      queryRdId={rdId}
      rdSlug={detail.slug}
      rdName={detail.name}
      sessionCount={detail.sessionCount}
      createdAt={detail.createdAt}
      rdSupervisorUserId={detail.supervisedByUserId}
      rdSupervisorName={detail.supervisorName}
      healthieLink={detail.healthieLink}
      profile={profile.data.data}
      snapshots={snapshots.data.data}
      initialEditFormId={initialEditFormId}
      openProfileSetup={search.profile === "setup"}
    />
  );
}

/** The titled band above one form snapshot: what it is, when, and where from. */
function SnapshotCardHeader({
  snapshot,
  rdSlug,
}: {
  snapshot: FormSnapshotData;
  rdSlug: string;
}): ReactElement {
  const isWeekly = snapshot.formType === "weekly_form";

  return (
    <div className="border-b border-border bg-muted px-5 py-4">
      <p className="font-sans text-eyebrow font-extrabold uppercase tracking-[0.08em] text-subtle">
        {isWeekly ? "Source · Weekly form" : "Source · Monthly checkpoint"}
      </p>
      <div className="mt-1.5 flex items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 font-sans text-heading-sm font-bold leading-[1.25] text-foreground">
          {snapshotWeekLabel(snapshot)}
          <Badge variant="category">{isWeekly ? "Weekly" : "Monthly"}</Badge>
        </h1>
        <AskAboutSourceAction
          rdSlug={rdSlug}
          source={{ type: "form", id: snapshot.id }}
        />
      </div>
      <p className="mt-1.5 text-xs leading-[1.45] text-subtle">
        {formSnapshotOriginLine(snapshot)}
      </p>
    </div>
  );
}

/**
 * Read-only full-page view of a single form snapshot — the flyover's "Open full
 * form" target and the eventual citation deep-link. Redirects to the RD landing
 * route when the formId is not one of the RD's snapshots (no 404).
 */
export function FormSnapshotPage(): ReactElement | null {
  const { rdId, formId } = useParams({ strict: false }) as {
    rdId: string;
    formId: string;
  };
  const { getToken } = useAuth();
  const rd = useRdDetail(rdId);
  const snapshots = useQuery({
    queryKey: ["forms", rdId],
    queryFn: () =>
      authenticatedApi<{ data: FormSnapshotData[] }>(
        getToken,
        `/api/rds/${rdId}/forms`
      ),
  });

  const detail = rd.data?.data;
  if (!detail || !snapshots.data) return <SurfaceLoading />;

  const snapshot = snapshots.data.data.find((s) => s.id === formId) ?? null;

  if (!snapshot) {
    return <Navigate to="/rds/$rdId" params={{ rdId }} replace />;
  }

  const prior = priorWeeklySnapshot(snapshots.data.data, snapshot);

  return <FormSnapshotContent detail={detail} snapshot={snapshot} prior={prior} />;
}

function FormSnapshotContent({
  detail,
  snapshot,
  prior,
}: {
  detail: RdDetailData;
  snapshot: FormSnapshotData;
  prior: FormSnapshotData | null;
}): ReactElement {
  const canRename = useCanRenameRd();

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto flex max-w-[920px] flex-col gap-4 px-6 py-6">
        <BreadcrumbSetter
          segments={[
            { label: "RDs", href: "/rds" },
            { label: detail.name, href: `/rds/${detail.slug}` },
            { label: "Functional Forms", href: `/rds/${detail.slug}/forms` },
            { label: snapshotWeekLabel(snapshot) },
          ]}
        />
        <RdHeaderStrip
          rdId={snapshot.rdId}
          canEdit={canRename}
          initialName={detail.name}
          sessionCount={detail.sessionCount}
          createdAt={detail.createdAt}
          supervisorName={detail.supervisorName}
          healthieLink={detail.healthieLink}
        />

        <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_2px_rgba(40,3,15,0.05)]">
          <SnapshotCardHeader snapshot={snapshot} rdSlug={detail.slug} />
          <div className="px-5 py-5">
            <FormSnapshotBody snapshot={snapshot} prior={prior} />
          </div>
        </section>
      </div>
    </div>
  );
}
